import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { locationsCatalogService, type Location } from '@/api/domains/catalog';
import { useUI, useCatalogLoader } from '@/hooks/ui';
import type { SelectorOption } from '@/components/ui';


export interface UseLocationSelectorReturn {
  locationId: string;
  locations: Location[];
  locationOptions: SelectorOption[];
  isLoadingLocations: boolean;
  handleLocationChange: (value: string | string[]) => void;
}

export function useLocationSelector(): UseLocationSelectorReturn {
  const { t: translate } = useTranslation();
  const { showNotification } = useUI();

  const [locationId, setLocationId] = useState<string>('');

  const catalog = useCatalogLoader<Location>(
    (signal) => locationsCatalogService.getLocations(signal),
    (location) => ({ value: String(location.id), label: location.name }),
    [],
    false,
    () => showNotification('error', translate('common:errors.loadingData'), 5000),
  );

  // Auto-select first location when catalog loads
  useEffect(() => {
    if (!locationId && catalog.options.length > 0) {
      setLocationId(catalog.options[0].value);
    }
  }, [catalog.options, locationId]);

  // Handle location change
  const handleLocationChange = useCallback((value: string | string[]) => {
    const selectedValue = Array.isArray(value) ? value[0] ?? '' : value;
    setLocationId(selectedValue);
  }, []);

  return {
    locationId,
    locations: catalog.data,
    locationOptions: catalog.options as SelectorOption[],
    isLoadingLocations: !catalog.isLoaded,
    handleLocationChange,
  };
}
